import {useMemo} from 'react'

import {useQuantity} from '../../../fields'
import {getNumberOfDecimals, roundToDecimalsWithPow} from '../../../utils/math'
import {useOrderChangeContext} from '../context'

import {isPlusLoadEnabled} from './isPlusLoadEnabled'

/**
 * Plus load wrapper over standard quantity change.
 * Supports both quantity mode (exact m³) and fullLoads mode (truck-based).
 * In fullLoads mode, uses minQuantity as the base quantity for delta calculation.
 */
export const usePlusLoad = () => {
  const {processData: order} = useOrderChangeContext()
  const quantityReturn = useQuantity()
  const {quantityConfiguration, defaultQuantity = 0} = quantityReturn

  // In quantity mode, use the exact quantity. In fullLoads mode, use minQuantity.
  const formQuantity =
    quantityReturn.type === 'volume' ? quantityReturn.volume : quantityReturn.minQuantity

  // Use the ticketed quantity as the baseline for both display and submission.
  // When there's a quantity range (e.g. 11-16 M3) and the ticketed quantity (e.g. 12.4 M3)
  // exceeds the minimum order quantity (e.g. 11 M3), the ticketed quantity is the correct
  // "current quantity" — not the minimum. `assignedQuantity` carries the ticketed amount as
  // soon as the last truck is loaded (the same field `isLastTruckLoaded` relies on), whereas
  // `deliveredQuantity` is still 0 while trucks are loaded-but-not-yet-delivered — using it
  // would collapse the baseline back to the order minimum and allow an undeliverable load.
  const assignedQuantity = order.deliveryProgress?.assignedQuantity ?? 0
  const baselineQuantity = Math.max(defaultQuantity, assignedQuantity)

  return useMemo(() => {
    if (!isPlusLoadEnabled(order)) {
      return {
        isEnabled: false
      } as const
    }

    const numberOfDecimals = getNumberOfDecimals(quantityConfiguration.step)

    // Anchor the plus load delta to the ticketed baseline (never the order minimum) and
    // clamp at zero: before the user interacts, the form still holds the order default,
    // which can sit below the ticketed quantity. This keeps the displayed delta and total
    // consistent with what is actually submitted.
    const plusLoadQuantity = Math.max(
      0,
      roundToDecimalsWithPow(formQuantity - baselineQuantity, numberOfDecimals)
    )

    const setPlusLoadQuantity =
      quantityReturn.type === 'volume'
        ? (newPlusLoadQuantity: number) =>
            quantityReturn.setVolume(
              roundToDecimalsWithPow(newPlusLoadQuantity + baselineQuantity, numberOfDecimals)
            )
        : undefined

    return {
      isEnabled: true,
      plusLoadQuantity,
      setPlusLoadQuantity,
      // Provide range in the same format as quantityConfiguration for consistency.
      // Anchoring the bounds to the ticketed baseline guarantees the minimum submittable
      // volume (range.min + baselineQuantity) can never drop below what is already ticketed.
      range: {
        min: Math.max(
          0,
          roundToDecimalsWithPow(quantityConfiguration.min - baselineQuantity, numberOfDecimals)
        ),
        max: Math.max(
          0,
          roundToDecimalsWithPow(quantityConfiguration.max - baselineQuantity, numberOfDecimals)
        ),
        step: quantityConfiguration.step,
        uom: quantityConfiguration.uom,
        default: plusLoadQuantity,
        closeAt: 0
      },
      currentQuantity: baselineQuantity,
      newTotalQuantity: roundToDecimalsWithPow(
        baselineQuantity + plusLoadQuantity,
        numberOfDecimals
      )
    } as const
  }, [
    baselineQuantity,
    order,
    formQuantity,
    quantityReturn,
    quantityConfiguration.max,
    quantityConfiguration.min,
    quantityConfiguration.step,
    quantityConfiguration.uom
  ])
}
